// packages import
import { Autocomplete, CircularProgress, FormControl, ListItem, TextField, createFilterOptions } from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
// components
import { Alert } from '../../../common/Alert'
import { JudgeListingAutoComplete } from './JudgeListingAutoComplete'
// others
import { CourtsPayload, useFetchAllCourtsLazyQuery } from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY, NOT_FOUND_EXCEPTION, SOMETHING_WENT_WRONG } from '../../../../constants'
import { CaseContext } from '../../../../context'
import { titleize } from '../../../../utils'
import { CaseActionType } from '../../caseReducer'
import { FormErrors } from '../../../../interfaceTypes'

interface CourtsAutoCompleteProps { 
  isEdit?: boolean
}

export const CourtsAutoComplete: FC<CourtsAutoCompleteProps> = ({ isEdit }) => {
  const { dispatch, isNewJudgeOpen } = useContext(CaseContext)
  const [allCourts, setAllCourts] = useState<CourtsPayload['courts']>()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const {
    control,
    formState: { errors },
    setValue,
    getValues
  } = useFormContext()
  // Judge selection stays locked until a court is picked; a duplicated matter arrives with its court already set.
  const [currentCourt, setCurrentCourt] = useState<boolean>(!isEdit && !getValues('court')?.id)
  const courtCustomError = errors['court'] as FormErrors['court']

  const [fetchAllCourtsWithOutCase, { loading }] = useFetchAllCourtsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      if (message !== NOT_FOUND_EXCEPTION) {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          fetchAllCourts: { courts, response }
        } = data
        const { status } = response || {}

        if (status === 200 && courts) {
          allCourts ? setAllCourts([...allCourts, ...courts]) : setAllCourts(courts)
        }
      }
    }
  })

  useEffect(() => {
    fetchAllCourtsWithOutCase({
      variables: {
        courtsInput: {
          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })

    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    setAllCourts([])
    setCurrentPage(1)

    // eslint-disable-next-line
  }, [debouncedSearchTerm])

  return (
    <>
      <FormControl variant="outlined" fullWidth>
        <Controller
          name="court"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              fullWidth
              value={value}
              loading={loading}
              options={allCourts || []}
              getOptionLabel={option => {
                const { id, name, county } = option || {}
                const labelWithRole = `${titleize(name || '')} ${county ? `(${county})` : ''}`

                return id ? labelWithRole : ''
              }}
              filterOptions={createFilterOptions({ ignoreCase: true })}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(event, item, reason) => {
                onChange(item)
                setCurrentCourt(false)
                setValue('judgeUser', { id: '', email: '', firstName: '', lastName: '' })
                isNewJudgeOpen && dispatch({ type: CaseActionType.SET_NEW_jUDGE_OPEN, isNewJudgeOpen: false })

                if (reason === 'clear') {
                  setCurrentCourt(true)
                }
              }}
              onInputChange={(event, newInputValue) => {
                setSearchTerm(newInputValue)
              }}
              ListboxProps={{
                onScroll: event => {
                  const listboxNode = event.currentTarget
                  if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                    setCurrentPage(currentPage + 1)
                  }
                },
                role: 'list-box',
                style: {
                  maxHeight: '190px'
                }
              }}
              renderOption={(props, option) => {
                const key = `listItem-${option?.id}`
                const { name, county } = option || {}

                return (
                  <ListItem {...props} key={key} sx={{ textTransform: 'capitalize' }}>
                    {titleize(name || '')} {county ? `(${county})` : ''}
                  </ListItem>
                )
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!courtCustomError?.id?.message}
                  disabled={loading}
                  helperText={courtCustomError?.id?.message || ''}
                  label="Select Court"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          )}
        />
      </FormControl>

      <JudgeListingAutoComplete currentCourt={currentCourt} />
    </>
  )
}
